import {
  Box,
  Slider,
  Typography,
  TextField,
  Paper,
  Grid,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
} from '@mui/material';
import LibraryBooksOutlinedIcon from '@mui/icons-material/LibraryBooksOutlined';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState, useRef, useCallback } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import apiReq from 'core/api';
import Loader from 'components/Loader';
import { USE_NEW_SHELL } from 'core/flags';
import { PageShell, Button as ShellButton } from 'components/AppShell';
import { AI_SETTINGS_SYSTEM_PROMPT_PREFILL_KEY } from '../../prompthub/utils/systemPromptCategory';
import styles from './SystemPrompt.module.scss';

const DEBOUNCE_MS = 2000;

const AiSettingsPage = () => {
  const navigate = useNavigate();
  const currProject = useSelector((state) => state.reducer.project.id);
  const roleId = useSelector((state) => state.reducer.role?.id || null);
  const queryClient = useQueryClient();

  const { data, isLoading } = useQuery({
    queryKey: ['getSettings', currProject, roleId],
    queryFn: () => apiReq(`projects/ai-settings?project_id=${currProject}&role_id=${roleId}`),
    enabled: !!currProject,
    gcTime: 2000,
  });

  const [temperature, setTemperature] = useState(data?.generation_parameters?.temperature);
  const [tokens, setTokens] = useState(data?.generation_parameters?.max_tokens);
  const [text, setText] = useState(data?.system_prompt);
  const [pendingPrompt, setPendingPrompt] = useState(null);
  const debounceRef = useRef(null);

  useEffect(() => {
    if (data) {
      setTokens(data.max_tokens || data.generation_parameters?.max_tokens);
      setTemperature(data.temperature || data.generation_parameters?.temperature);
      setText(data.system_prompt ?? '');
    }
  }, [data]);

  const saveSettings = useCallback(
    (payload) => {
      if (!currProject) return;
      apiReq(`projects/ai-settings?project_id=${currProject}&role_id=${roleId}`, 'post', payload);
      queryClient.invalidateQueries({ queryKey: ['getSettings', currProject, roleId] });
    },
    [currProject, roleId, queryClient]
  );

  useEffect(() => {
    if (isLoading || !data || !currProject) return;
    let raw;
    try {
      raw = localStorage.getItem(AI_SETTINGS_SYSTEM_PROMPT_PREFILL_KEY);
      if (!raw) return;
      // Сразу убираем ключ, чтобы refresh страницы не открывал диалог повторно.
      localStorage.removeItem(AI_SETTINGS_SYSTEM_PROMPT_PREFILL_KEY);
      const content = JSON.parse(raw);
      if (typeof content !== 'string') return;
      // Не сохраняем молча: спрашиваем подтверждение перед заменой текущего промпта.
      setPendingPrompt(content);
    } catch {
      if (raw) localStorage.removeItem(AI_SETTINGS_SYSTEM_PROMPT_PREFILL_KEY);
    }
  }, [isLoading, data, currProject]);

  const handleConfirmReplace = () => {
    const content = pendingPrompt ?? '';
    const nextTemperature = data?.temperature ?? data?.generation_parameters?.temperature ?? 0;
    const nextMaxTokens = data?.max_tokens ?? data?.generation_parameters?.max_tokens ?? 1000;
    setText(content);
    setTemperature(nextTemperature);
    setTokens(nextMaxTokens);
    saveSettings({
      system_prompt: content,
      temperature: nextTemperature,
      max_tokens: nextMaxTokens,
    });
    setPendingPrompt(null);
  };

  const handleCancelReplace = () => {
    setPendingPrompt(null);
  };

  const handleTemperature = (event, newValue) => {
    setTemperature(newValue);
  };

  const handleTemperatureCommitted = (event, newValue) => {
    saveSettings({
      system_prompt: text ?? '',
      temperature: newValue,
      max_tokens: tokens ?? 1000,
    });
  };

  const handleTokens = (event, newValue) => {
    setTokens(newValue);
  };

  const handleTokensCommitted = (event, newValue) => {
    saveSettings({
      system_prompt: text ?? '',
      temperature: temperature ?? 0,
      max_tokens: newValue,
    });
  };

  const handlePrompt = (event) => {
    const value = event.target.value;
    setText(value);

    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      saveSettings({
        system_prompt: value,
        temperature: temperature ?? 0,
        max_tokens: tokens ?? 1000,
      });
    }, DEBOUNCE_MS);
  };

  useEffect(() => {
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, []);

  const grid = (
    <Grid container spacing={3}>
      <Grid item xs={12} lg={8}>
        <Paper className={styles.paper} elevation={0}>
          <TextField
            id="multiline-basic"
            label="Cистемный промпт"
            multiline
            rows={25}
            fullWidth
            value={text || ''}
            onChange={handlePrompt}
            variant="outlined"
            placeholder="Введите системный промпт для настройки поведения ИИ..."
            className={styles.textField}
          />
        </Paper>
      </Grid>

      <Grid item xs={12} lg={4}>
        <Paper className={styles.paper} elevation={0}>
          <Typography variant="h6" className={styles.sectionTitle} gutterBottom>
            Параметры генерации
          </Typography>

          <Box className={styles.sliderSection}>
            <Box className={styles.sliderContainer}>
              <Box className={styles.sliderHeader}>
                <Box className={styles.sliderTitleRow}>
                  <Typography variant="subtitle1" fontWeight={600}>
                    Температура
                  </Typography>
                  <Typography variant="subtitle1" className={styles.sliderValue}>
                    {temperature?.toFixed(2) ?? '0.00'}
                  </Typography>
                </Box>
                <Typography variant="body2" color="text.secondary">
                  Контролирует случайность ответов (0 = детерминированный, 1 = креативный)
                </Typography>
              </Box>

              <Slider
                value={temperature ?? 0}
                onChange={handleTemperature}
                onChangeCommitted={handleTemperatureCommitted}
                aria-labelledby="temperature-slider"
                valueLabelDisplay="auto"
                step={0.1}
                min={0}
                max={1}
                className={styles.slider}
              />
            </Box>

            <Box className={styles.sliderContainer}>
              <Box className={styles.sliderHeader}>
                <Box className={styles.sliderTitleRow}>
                  <Typography variant="subtitle1" fontWeight={600}>
                    Количество токенов
                  </Typography>
                  <Typography variant="subtitle1" className={styles.sliderValue}>
                    {tokens?.toLocaleString() ?? '1000'}
                  </Typography>
                </Box>
                <Typography variant="body2" color="text.secondary">
                  Максимальная длина ответа в токенах
                </Typography>
              </Box>

              <Slider
                value={tokens ?? 1000}
                onChange={handleTokens}
                onChangeCommitted={handleTokensCommitted}
                aria-labelledby="tokens-slider"
                valueLabelDisplay="auto"
                step={1000}
                min={1000}
                max={30000}
                className={styles.slider}
              />
            </Box>
          </Box>
        </Paper>
      </Grid>
    </Grid>
  );

  const confirmDialog = (
    <Dialog open={pendingPrompt !== null} onClose={handleCancelReplace}>
      <DialogTitle>Заменить системный промпт?</DialogTitle>
      <DialogContent>
        <DialogContentText>
          Текущий системный промпт будет заменён шаблоном из хранилища и сохранён. Действие
          необратимо.
        </DialogContentText>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleCancelReplace}>Отмена</Button>
        <Button color="primary" variant="contained" onClick={handleConfirmReplace}>
          Заменить и сохранить
        </Button>
      </DialogActions>
    </Dialog>
  );

  if (USE_NEW_SHELL) {
    return (
      <PageShell
        title="Настройки ИИ"
        subtitle="Системный промпт и параметры генерации"
        actions={
          <ShellButton
            variant="secondary"
            leftIcon={<LibraryBooksOutlinedIcon style={{ fontSize: 16 }} />}
            onClick={() => navigate('/prompthub')}
          >
            Хранилище промптов
          </ShellButton>
        }
      >
        {isLoading ? <Loader /> : grid}
        {confirmDialog}
      </PageShell>
    );
  }

  return (
    <>
      {isLoading ? (
        <Loader />
      ) : (
        <Box className={styles.pageContainer}>
          <Box className={styles.titleRow}>
            <Typography variant="h4" className={styles.pageTitle} component="h1">
              Настройки ИИ
            </Typography>
            <Button
              variant="outlined"
              color="primary"
              startIcon={<LibraryBooksOutlinedIcon />}
              onClick={() => navigate('/prompthub')}
            >
              Хранилище промптов
            </Button>
          </Box>
          {grid}
        </Box>
      )}
      {confirmDialog}
    </>
  );
};

export default AiSettingsPage;
